import { useCallback, useEffect, useState } from 'react';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';
import type { BetaAccessMode, BetaAllowlistEntry, SystemStatus } from '../types/system';
import type { BetaWelcomeDmSettings } from '../types/beta-welcome';
import BetaAllowlistPanel from './BetaAllowlistPanel';
import BetaFeedbackAdminPanel from './BetaFeedbackAdminPanel';
import BetaKnownIssuesAdmin from './BetaKnownIssuesAdmin';
import BetaWelcomeAdmin from './BetaWelcomeAdmin';
import BetaWelcomeDmAdmin from './BetaWelcomeDmAdmin';
import {
  BetaAdminActions,
  BetaAdminCard,
  BetaAdminModeOption,
} from './BetaAdminUi';
import '../styles/admin-beta.css';

function adminFetch(url: string, pseudo: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'X-Admin-Pseudo': pseudo,
    },
  });
}

interface AdminBetaStatus {
  mode: BetaAccessMode;
  allowlist: BetaAllowlistEntry[];
  envMode: BetaAccessMode;
  envAllowlistSize: number;
  allowedModes?: BetaAccessMode[];
  allowlistEnabled?: boolean;
}

export type BetaAdminSection = 'ops' | 'inbox';

interface AdminBetaOpsPanelProps {
  session: Session;
  section: BetaAdminSection;
  onSectionChange: (section: BetaAdminSection) => void;
}

const MODE_OPTIONS: Array<{
  value: BetaAccessMode;
  label: string;
  description: string;
}> = [
  { value: 'open', label: 'Ouvert', description: 'Tous les comptes webtarot peuvent accéder à Neo.' },
  { value: 'closed', label: 'Fermé', description: 'Admins uniquement — Neo n’est pas ouvert au public.' },
];

const MODE_LABELS: Record<BetaAccessMode, string> = {
  open: 'Ouvert',
  whitelist: 'Whitelist',
  closed: 'Fermé',
};

export default function AdminBetaOpsPanel({ session, section, onSectionChange }: AdminBetaOpsPanelProps) {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [beta, setBeta] = useState<AdminBetaStatus | null>(null);
  const [betaMode, setBetaMode] = useState<BetaAccessMode>('open');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [welcomeDmEnabled, setWelcomeDmEnabled] = useState(false);

  const loadAll = useCallback(async () => {
    const [statusRes, betaRes] = await Promise.all([
      adminFetch(`${BACKEND_URL}/api/admin/system/status?pseudo=${encodeURIComponent(session.pseudo)}`, session.pseudo),
      adminFetch(`${BACKEND_URL}/api/admin/system/beta?pseudo=${encodeURIComponent(session.pseudo)}`, session.pseudo),
    ]);

    if (statusRes.ok) {
      setStatus(await statusRes.json() as SystemStatus);
    }

    if (betaRes.ok) {
      const data = await betaRes.json() as { beta: AdminBetaStatus };
      setBeta(data.beta);
      setBetaMode(data.beta.mode);
    }
  }, [session.pseudo]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const saveBetaMode = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/mode?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: betaMode }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setBeta(data.beta);
      setFeedback('Mode bêta mis à jour');
      await loadAll();
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  if (!status || !beta) {
    return <div className="admin-empty">Chargement…</div>;
  }

  const modeChanged = betaMode !== beta.mode;
  const isPreprod = status.appEnv === 'preprod' || beta.allowlistEnabled === true;
  const visibleModes = MODE_OPTIONS.filter(option =>
    (beta.allowedModes ?? (isPreprod ? ['whitelist'] : ['open', 'closed'])).includes(option.value),
  );

  return (
    <div className={`beta-admin-page${section === 'inbox' ? ' beta-admin-page--inbox' : ''}`}>
      <header className="beta-admin-hero">
        <div>
          <p className="beta-admin-kicker">Exploitation Neo</p>
          <h2 className="beta-admin-title">Bêta-test</h2>
        </div>
        <div className="beta-admin-pills">
          <span className="beta-admin-pill">{status.appEnv}</span>
          <span className="beta-admin-pill beta-admin-pill--accent">
            {isPreprod ? 'Bêta-testeurs' : `Accès : ${MODE_LABELS[beta.mode]}`}
          </span>
          {isPreprod && (
            <span className="beta-admin-pill">
              {beta.allowlist.length} testeur{beta.allowlist.length !== 1 ? 's' : ''}
            </span>
          )}
          <span className="beta-admin-pill">Build {status.buildId}</span>
        </div>
      </header>

      <div className="beta-admin-sections-nav">
        <button
          type="button"
          className={`ml-section-btn${section === 'ops' ? ' active' : ''}`}
          onClick={() => onSectionChange('ops')}
        >
          Exploitation
        </button>
        <button
          type="button"
          className={`ml-section-btn${section === 'inbox' ? ' active' : ''}`}
          onClick={() => onSectionChange('inbox')}
        >
          Retours testeurs
        </button>
      </div>

      {feedback && (
        <p className="beta-admin-toast" role="status">{feedback}</p>
      )}

      {section === 'ops' ? (
        <div className="beta-admin-stack">
          {isPreprod ? (
            <BetaAdminCard
              icon="🔐"
              title="Accès preprod"
              description="Cette instance est toujours en bêta-test : admins et liste ci-dessous uniquement."
            >
              <p className="beta-admin-note">
                Les joueurs non listés ne peuvent pas se connecter. La prod, elle, est soit ouverte à tous, soit fermée (admins seuls).
              </p>
            </BetaAdminCard>
          ) : (
            <BetaAdminCard
              icon="🔐"
              title="Accès à Neo"
              description="Ouvert à tous les comptes, ou fermé (admins uniquement). Pas de liste de testeurs en prod."
              footer={(
                <BetaAdminActions align="end">
                  <button
                    type="button"
                    className="beta-admin-btn beta-admin-btn--primary"
                    disabled={busy || !modeChanged}
                    onClick={() => void saveBetaMode()}
                  >
                    Appliquer le mode
                  </button>
                </BetaAdminActions>
              )}
            >
              <div className="beta-admin-mode-grid">
                {visibleModes.map(option => (
                  <BetaAdminModeOption
                    key={option.value}
                    label={option.label}
                    description={option.description}
                    active={betaMode === option.value}
                    current={beta.mode === option.value}
                    disabled={busy}
                    onSelect={() => setBetaMode(option.value)}
                  />
                ))}
              </div>
            </BetaAdminCard>
          )}

          {isPreprod && (
            <BetaAdminCard
              icon="👥"
              title="Bêta-testeurs"
              description="Gérez la whitelist et ajoutez de nouveaux testeurs."
              className="beta-admin-card--span-2"
            >
              <BetaAllowlistPanel
                session={session}
                entries={beta.allowlist}
                busy={busy}
                defaultSendWelcomeDm={welcomeDmEnabled}
                onChanged={loadAll}
                onFeedback={setFeedback}
                onBusyChange={setBusy}
              />
            </BetaAdminCard>
          )}

          <div className="beta-admin-grid-2">
            <BetaWelcomeAdmin
              session={session}
              knownIssuesPreview={status.beta.knownIssues}
              busy={busy}
              onBusyChange={setBusy}
              onFeedback={setFeedback}
            />

            <BetaKnownIssuesAdmin
              session={session}
              busy={busy}
              onBusyChange={setBusy}
              onFeedback={setFeedback}
            />
          </div>

          <BetaWelcomeDmAdmin
            session={session}
            busy={busy}
            onBusyChange={setBusy}
            onFeedback={setFeedback}
            onSettingsChange={(settings: BetaWelcomeDmSettings) => setWelcomeDmEnabled(settings.enabled)}
          />
        </div>
      ) : (
        <BetaFeedbackAdminPanel session={session} />
      )}
    </div>
  );
}
